// src/context/firebase/hooks/useGroups.ts
import { useCallback, useMemo, useState, useEffect } from 'react';
import { useFirebaseContext } from 'features/user-management/auth/context/FirebaseContext';
import firebaseServices from 'core/services/firebase';
import { StoredImage } from 'core/types/storedImage';
import type { CreateGroupInput } from 'core/types/user';

export function useGroups() {
  const {
    user,
    groups,
    activeGroupId,
    activeGroupUserProfile,
    setError,
    refreshGroups,
    switchGroup: switchGroupContext,
  } = useFirebaseContext();

  // Track a more accurate loading state
  const [fullyLoaded, setFullyLoaded] = useState(false);


  // Derive activeGroup directly from groups and activeGroupId
  const activeGroup = useMemo(() => {
    if (!groups.length || !activeGroupId) {
      return null;
    }

    const found = groups.find(g => g.id === activeGroupId);
    return found || null;
  }, [groups, activeGroupId]);

  // Memoized isAdmin status
  const isAdmin = useMemo(() => {
    if (!activeGroupUserProfile) return false;
    // Check if the role is admin (case-insensitive to avoid potential issues)
    return activeGroupUserProfile.role?.toLowerCase() === 'admin';
  }, [activeGroupUserProfile]);

  // When we have all the data we need, mark as fully loaded
  useEffect(() => {
    // Consider fully loaded when we have a user and either:
    // 1. We have groups loaded (even if empty array)
    // 2. We have an active group profile
    if (user && (Array.isArray(groups) || activeGroupUserProfile)) {
      setFullyLoaded(true);
    }
  }, [user, groups, activeGroupUserProfile]);

  // Create a new group
  const createGroup = useCallback(async (input: CreateGroupInput): Promise<string> => {
    try {
      setError(null);
      const groupId = await firebaseServices.group.createGroup(input);
      await refreshGroups();
      return groupId;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create group');
      throw err;
    }
  }, [setError, refreshGroups]);

  /**
   * Rename the active group, or change its description (admin only, T036).
   *
   * Refreshes the group list afterwards, since every surface that names the
   * group -- the header's account menu, the campaign picker, the admin pages --
   * reads it from there.
   */
  const updateGroup = useCallback(async (
    name: string,
    description?: string
  ): Promise<void> => {
    try {
      setError(null);

      if (!activeGroupId) {
        throw new Error('No active group selected');
      }

      await firebaseServices.group.updateGroup(activeGroupId, { name, description });
      await refreshGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update the group');
      throw err;
    }
  }, [activeGroupId, setError, refreshGroups]);

  /**
   * Set or clear the active group's crest (admin only, T021), then refresh the
   * group list the crest is read from.
   */
  const setGroupCrest = useCallback(async (crest: StoredImage | null): Promise<void> => {
    if (!activeGroupId) {
      throw new Error('No active group selected');
    }
    await firebaseServices.group.setGroupCrest(activeGroupId, crest);
    await refreshGroups();
  }, [activeGroupId, refreshGroups]);

  // Switch active group (alias for setActiveGroup for backward compatibility)
  //
  // The write and the context update both live in FirebaseContext, because
  // only the provider can move `activeGroupId` in React state. Writing the
  // profile here and calling `refreshGroups()` -- what this did before -- left
  // the context on the old group and needed a page reload to take effect.
  const switchGroup = useCallback(async (
    groupId: string
  ): Promise<void> => {
    try {
      setError(null);
      await switchGroupContext(groupId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to switch group');
      throw err;
    }
  }, [setError, switchGroupContext]);

  // Set active group (original name)
  const setActiveGroup = switchGroup;

  // Join group with token
  const joinGroupWithToken = useCallback(async (
    token: string,
    username: string
  ): Promise<void> => {
    try {
      setError(null);
      await firebaseServices.invitation.joinGroupWithToken(token, username);
      await refreshGroups();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to join group with token');
      throw err;
    }
  }, [setError, refreshGroups]);

  // Get all users in the active group
  const getAllUsers = useCallback(async () => {
    try {
      setError(null);
      
      if (!activeGroupId) {
        throw new Error('No active group selected');
      }
      
      // Use the memoized isAdmin value for this check
      if (!isAdmin) {
        throw new Error('Only admins can view user list');
      }
      
      return await firebaseServices.group.getGroupUsers(activeGroupId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch users');
      throw err;
    }
  }, [activeGroupId, isAdmin, setError]);

  // Remove a user from the active group
  const removeUser = useCallback(async (
    userId: string
  ): Promise<void> => {
    try {
      setError(null);
      
      if (!activeGroupId) {
        throw new Error('No active group selected');
      }
      
      await firebaseServices.group.removeUserFromGroup(activeGroupId, userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove user');
      throw err;
    }
  }, [activeGroupId, setError]);

  // Delete a user from the active group
  const deleteUser = useCallback(async (userId: string): Promise<void> => {
    try {
      setError(null);
      
      if (!activeGroupId) {
        throw new Error('No active group selected');
      }
      
      await firebaseServices.group.removeUserFromGroup(activeGroupId, userId);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete user');
      throw err;
    }
  }, [activeGroupId, setError]);

  /**
   * Make a member of the active group an admin, or an admin a member (T034).
   *
   * The server refuses a change that would leave the group with no admin
   * (T035), and its message says so; callers show it as it comes.
   */
  const setMemberRole = useCallback(async (
    userId: string,
    role: 'admin' | 'member'
  ): Promise<void> => {
    try {
      setError(null);

      if (!activeGroupId) {
        throw new Error('No active group selected');
      }

      await firebaseServices.group.setMemberRole(activeGroupId, userId, role);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to change the role');
      throw err;
    }
  }, [activeGroupId, setError]);

  // Create an improved loading state that sets to false once we have all required data
  const loading = !fullyLoaded;

  return {
    user,
    groups,
    activeGroupId,
    activeGroup,
    activeGroupUserProfile,
    createGroup,
    updateGroup,
    setGroupCrest,
    setActiveGroup,
    switchGroup,          
    joinGroupWithToken,   
    refreshGroups,
    getAllUsers,
    removeUser,
    deleteUser,
    setMemberRole,
    isAdmin,
    loading
  };
}